import { useEffect } from "react";
import {
  useLoaderData,
  useParams,
  Link,
  Form,
  useNavigation,
  useActionData,
} from "react-router-dom";
import { toast } from "react-hot-toast";
import { useAuth } from "../../hooks/useAuth";
import { Header } from "../../components/layout/Header";
import { Button } from "../../components/common";
import type { AddTeamMemberData } from "../../routes/loaders";
import type { ActionResult } from "../../routes/actions";
import type { Role } from "../../types/user";
import styles from "./AddTeamMemberPage.module.css";

export default function AddTeamMemberPage() {
  const { id: orgId, teamId } = useParams<{ id: string; teamId: string }>();
  const { team, currentMemberIds, organizationMembers } = useLoaderData() as AddTeamMemberData;
  const actionData = useActionData() as ActionResult | undefined;
  const navigation = useNavigation();
  const { user } = useAuth();

  const userRole: Role | undefined = user?.organizations.find((org) => org.id === orgId)?.role;

  const canManageTeams = userRole === "owner" || userRole === "admin";
  const isSubmitting = navigation.state === "submitting";

  const availableMembers = organizationMembers.filter(
    (member) => !currentMemberIds.includes(member.userId),
  );

  useEffect(() => {
    if (actionData && !actionData.success && actionData.error && navigation.state === "idle") {
      toast.error(actionData.error);
    }
  }, [actionData, navigation.state]);

  if (!canManageTeams) {
    return (
      <>
        <Header />
        <div className={styles.container}>
          <p>You do not have permission to add team members.</p>
          <Link to={`/organizations/${orgId}/teams/${teamId}`}>Back to Team</Link>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className={styles.container}>
        <Link to={`/organizations/${orgId}/teams/${teamId}`} className={styles.backLink}>
          Back to Team
        </Link>
        <h1 className={styles.title}>Add Member to {team.name}</h1>

        <div className={styles.card}>
          {availableMembers.length === 0 ? (
            <p className={styles.emptyMessage}>
              All organization members are already in this team.
            </p>
          ) : (
            <Form method="post">
              <div className={styles.formGroup}>
                <label htmlFor="userId" className={styles.label}>
                  Select Member
                </label>
                <select
                  id="userId"
                  name="userId"
                  required
                  className={styles.select}
                  defaultValue=""
                >
                  <option value="" disabled>
                    -- Select a member --
                  </option>
                  {availableMembers.map((member) => (
                    <option key={member.userId} value={member.userId}>
                      {member.email} ({member.role})
                    </option>
                  ))}
                </select>
                {actionData?.fieldErrors?.userId && (
                  <p className="error-text">{actionData.fieldErrors.userId}</p>
                )}
              </div>

              <Button
                type="submit"
                variant="primary"
                fullWidth
                loading={isSubmitting}
                loadingText="Adding..."
              >
                Add Member
              </Button>
            </Form>
          )}
        </div>
      </div>
    </>
  );
}
